import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import SuperMultiAutocomplete from 'components/forms/SuperMultiAutocomplete'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import ContributorAction from '../actions/ContributorAction'
import { Icon } from '@mui/material'
import { FROZEN_CODE_STATUS } from 'station/constants/StationConstants'
import ContributorItem from 'referencial/components/contributor/dto/ContributorItem'
import ProducerDto from '../dto/ProducerDto'
import { uniq } from 'lodash'

const MultiContributorsAutocomplete = ({
    col,
    className = '',
    multiple = false,
    limit = 1,
    onChange = () => { },
    label,
    values,
    disabled = false,
    options,
    keyLabel = 'name',
    keyValue = 'id',
    obligatory,
    disablePortal = true,
    placeholder,
    withGroups = false,
    'data-cy': datacy,
}) => {
    const {
        contributorsGroups,
    } = useSelector(store => ({
        contributorsGroups: store.ContributorReducer.contributorsGroups,
    }), shallowEqual)

    const dispatch = useDispatch()

    const [data, setData] = useState(multiple ? [] : undefined)

    useEffect(() => {
        if (!contributorsGroups.length) {
            dispatch(ContributorAction.fetchContributorsGroups())
        }
    }, [])

    useEffect(() => {
        if (!multiple) {
            setData(values)
        } else if (multiple) {
            if (values?.length) {
                setData(prev => uniq([...prev, ...values]))
            } else {
                setData([])
            }
        }
    }, [multiple, values])

    const contributorsFormatted = useMemo(() => options.map(c => {
        const nameFormatted = c.labelDisplay
        return {
            ...c,
            name: nameFormatted,
            endIcon: c?.status === FROZEN_CODE_STATUS && (
                <Icon fontSize='small' style={{ marginLeft: '1rem' }}>lock</Icon>
            ),
        }
    }), [options])

    const contributorsGroupsFormatted = useMemo(() => withGroups ? contributorsGroups.map(cg => ({ ...cg, elements: cg.contributors })) : [], [contributorsGroups, withGroups])

    const handleChange = (value) => {
        setData(value)
        if (value?.length > 0 || !multiple) {
            onChange(value)
        } else {
            onChange(undefined)
        }
    }

    return (
        <SuperMultiAutocomplete
            className={className}
            options={contributorsFormatted}
            optionsGroups={contributorsGroupsFormatted}
            keyLabel={keyLabel}
            keyValue={keyValue}
            label={label ?? i18n.organism}
            col={col}
            onChange={handleChange}
            values={data}
            multiple={multiple}
            groupIcon='domain'
            additionalKeysFilter={['groupTitle', 'siret', 'sandre', 'id']}
            limit={limit}
            groupsCanBeSelected={multiple}
            disabled={disabled}
            obligatory={obligatory}
            disablePortal={disablePortal}
            placeholder={placeholder}
            data-cy={datacy}
        />
    )
}

MultiContributorsAutocomplete.propTypes = {
    /**
     * @deprecated please use the MUI Grid
     */
    col: PropTypes.number,
    className: PropTypes.string,
    multiple: PropTypes.bool,
    limit: PropTypes.number,
    onChange: PropTypes.func,
    label: PropTypes.string,
    values: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.number),
        PropTypes.arrayOf(PropTypes.string),
        PropTypes.number,
        PropTypes.string,
    ]),
    disabled: PropTypes.bool,
    options: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.instanceOf(ContributorItem)),
        PropTypes.arrayOf(PropTypes.instanceOf(ProducerDto)),
    ]),
    keyValue: PropTypes.string,
    keyLabel: PropTypes.string,
    obligatory: PropTypes.bool,
    disablePortal: PropTypes.bool,
    placeholder: PropTypes.string,
    withGroups: PropTypes.bool,
    'data-cy': PropTypes.string,
}

export default MultiContributorsAutocomplete